import { MouseEvent, useEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router-dom'
import cn from 'classnames'

import { useValueContext } from 'contexts'
import { IEvent } from 'types'
import { EventIcon, PencilIcon, Spinner } from 'components'
import {
  calculateDurationInSecond,
  formatDate,
  secondsInHHFormat,
  secondsInTimeFormat,
} from 'utils'

import { eventTypes } from '../../constants'
import './styles.scss'

const dateFormat = 'HH:mm'

export const CurrentEventsList = observer(() => {
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const { store } = useValueContext()
  const navigate = useNavigate()
  const { currentEvents, currentLoading, refreshDuration, stopEvent } = store

  const hasEvents = !!currentEvents?.length

  useEffect(() => {
    if (hasEvents && !timerRef.current) {
      timerRef.current = setInterval(() => {
        refreshDuration()
      }, 1000)
    } else if (!hasEvents && timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [hasEvents, refreshDuration])

  if (currentLoading) {
    return (
      <div className="current-events-container">
        <Spinner variant="sm" open />
      </div>
    )
  }

  return hasEvents ? (
    <div className="current-events-container">
      <div className="current-events-list">
        {currentEvents?.map((event: IEvent) => {
          const { id, eventTypeCode, startTime, endTime } = event
          const eventType = eventTypes.find(({ type }) => type === eventTypeCode)

          const duration = calculateDurationInSecond(startTime, endTime)
          const mmss = secondsInTimeFormat(duration)
          const hh = secondsInHHFormat(duration)

          const onStop = async (e: MouseEvent) => {
            e.stopPropagation()
            stopEvent(id as number)
          }

          const onNavigate = () =>
            navigate(`/event/${id}`, { state: { current: true, event: { ...event } } })

          return (
            <div className="events-list-item" key={id}>
              {eventType && (
                <EventIcon
                  className="event-icon"
                  iconName={eventType.icon}
                  iconProps={{ width: 22, height: 22 }}
                  width={39}
                  height={39}
                />
              )}
              <div className="event-wrapper">
                <div className="event-info">
                  <div className="start-time">{formatDate(startTime, dateFormat)}</div>
                  {eventType && <div className="title">{eventType?.title}</div>}
                </div>
                <div className="edit-duration">
                  <PencilIcon
                    class="edit-button"
                    fill="black"
                    width={16}
                    height={16}
                    onClick={onNavigate}
                  />
                  <div className="duration-container">
                    <div className={cn({ 'inactive-duration': hh === '00' })}>{hh}:</div>
                    <div className="mins-secs">{mmss}</div>
                    <div className="stop-button" onClick={onStop} />
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  ) : null
})
